import React, { useEffect } from 'react'
import { connect } from 'react-redux'
import styled from 'styled-components/native'
import { SafeAreaView } from 'react-native-safe-area-context'
import type { Dispatch } from 'redux'

import { useDispatch } from 'APP/Hooks/redux'
import StartupActions from 'APP/Redux/StartupRedux'
import PatientActions from 'APP/Redux/PatientRedux'

import I18n from 'APP/I18n'

import { useDevTools } from 'APP/Lib/DevTools'

import { Images } from 'APP/Themes'

import List from 'APP/Components/List'
import BulletListItem from 'APP/Components/BulletListItem'
import LogoHeader from 'APP/Components/LogoHeader'
import Button from 'APP/Converse/Button'
import Typography from 'APP/Converse/Typography'
import { ResponsiveView } from 'APP/Converse/Layout'

const ScreenContainer = styled(SafeAreaView).attrs({
  edges: ['bottom', 'left', 'right'],
})`
  flex: 1;
`

const ContentContainer = styled(ResponsiveView)`
  gap: ${({ theme }) => theme.metrics.baseMargin * 2}px;
  padding: ${({ theme }) => theme.metrics.baseMargin}px;
`

const InnerContentContainer = styled.View`
  gap: ${({ theme }) => theme.metrics.baseMargin}px;
`

interface PushNotificationPermissionScreenProps {
  postNotificationsRequest: () => void
}

const PushNotificationPermissionScreen: React.FC<PushNotificationPermissionScreenProps> = ({
  postNotificationsRequest,
}) => {
  const { setToolFunction, setToolName } = useDevTools()
  const dispatch = useDispatch()

  useEffect(() => {
    setToolName('Allow')
    setToolFunction(() => () => {
      dispatch(StartupActions.setNotificationPermissionAccepted(true))
    })
    return () => {
      setToolFunction(null)
      setToolName(null)
    }
  }, [dispatch, setToolFunction, setToolName])

  function renderTexts() {
    const bodyNotifyBulletPoints = I18n.t(
      'PushNotificationPermissionScreen.bodyNotifyBulletPoints',
      { returnObjects: true }
    ) as string[]

    const renderBulletListItem = (item: string) => {
      return (
        <BulletListItem>
          <Typography>{item}</Typography>
        </BulletListItem>
      )
    }

    return (
      <>
        <Typography variant="subheader">
          {I18n.t('PushNotificationPermissionScreen.bodyNotify')}
        </Typography>
        <List>
          {renderBulletListItem(bodyNotifyBulletPoints[0])}
          {renderBulletListItem(bodyNotifyBulletPoints[1])}
        </List>
      </>
    )
  }

  function renderSubtitle() {
    const bodyAllow = I18n.t('PushNotificationPermissionScreen.bodyAllow', {
      returnObjects: true,
    }) as string[]

    return (
      <Typography>
        {bodyAllow[0]} <Typography variant="bodyNormalBold">{bodyAllow[1]}</Typography>
      </Typography>
    )
  }

  return (
    <ScreenContainer>
      <ContentContainer>
        <LogoHeader
          source={Images.logoBlue}
          title={I18n.t('PushNotificationPermissionScreen.title')}
          subtitle={renderSubtitle()}
        />
        <InnerContentContainer>{renderTexts()}</InnerContentContainer>
        <Button
          variant="primary"
          widthVariant="full"
          title={I18n.t('PushNotificationPermissionScreen.cta')}
          analyticsName={'PushNotificationPermissionScreen.allowNotifications'}
          onPress={() => postNotificationsRequest()}
        />
      </ContentContainer>
    </ScreenContainer>
  )
}

interface OwnProps {
  route?: { params?: Record<string, unknown> }
}

const mapDispatchToProps = (dispatch: Dispatch, props: OwnProps) => {
  return {
    ...props.route?.params,
    postNotificationsRequest: () => dispatch(PatientActions.postNotificationsRequest()),
  }
}

export default connect(null, mapDispatchToProps)(PushNotificationPermissionScreen)
